import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, Search, LogOut, User, Settings, ShieldCheck } from "lucide-react";
import { trpc } from "../../hooks/trpc";
import { INSTANCE } from "../../lib/instance";
import { NoteSearchDropdown } from "./NoteSearchDropdown";

// PRODUCT_NAME "COMBSOL/OS" → "combsol" + gedimmt "/OS"; "CORE" → "core".
const BRAND_SLASH = INSTANCE.productName.indexOf("/");
const BRAND_HEAD = (BRAND_SLASH >= 0 ? INSTANCE.productName.slice(0, BRAND_SLASH) : INSTANCE.productName).toLowerCase();
const BRAND_TAIL = BRAND_SLASH >= 0 ? INSTANCE.productName.slice(BRAND_SLASH) : "";

type Props = {
  appId: string;
  breadcrumb: string;
  initials: string;
  showAdministration?: boolean;
};

export function Topbar({ appId, breadcrumb, initials, showAdministration = false }: Props) {
  const [query, setQuery] = useState("");
  const [showMenu, setShowMenu] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const searchWrapRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const logoutMutation = trpc.auth.logout.useMutation();

  // Close menu on outside click
  useEffect(() => {
    if (!showMenu) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showMenu]);

  const handleLogout = async () => {
    await logoutMutation.mutateAsync();
    navigate("/login");
  };

  return (
    <div className="os-topbar" data-app={appId}>
      {/* Hub Logo — zentraler Home-Button fuer den App-Launcher */}
      <Link
        to="/home"
        className="os-brand"
        style={{ textDecoration: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: "10px" }}
        title="Home öffnen"
      >
        <img
          src={INSTANCE.logoUrl}
          alt="Hub"
          style={{ width: 34, height: 34, borderRadius: 8 }}
        />
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span className="os-brand-text">
            {BRAND_HEAD}{BRAND_TAIL ? <span className="dim">{BRAND_TAIL}</span> : null}
          </span>
          <span className="os-brand-subline">{INSTANCE.productSubtitle}</span>
        </div>
      </Link>

      <div className="os-breadcrumb">
        <span className="crumb-dot" />
        <span className="crumb-current">{breadcrumb}</span>
        <span className="sep">·</span>
        <span>v 2.0</span>
      </div>

      <div className="os-top-actions">
        <div ref={searchWrapRef} className="os-search-wrap" style={{ position: "relative" }}>
          <span className="os-search-icon">
            <Search size={13} strokeWidth={1.8} />
          </span>
          <input
            className="os-search-input"
            placeholder={appId === "notes" ? "Nexus durchsuchen…" : "Suchen…"}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            aria-label="Globale Suche"
          />
          {query.length === 0 && (
            <span className="os-kbd" aria-hidden>⌘K</span>
          )}
          {appId === "notes" && searchFocused && query.length >= 2 && (
            <NoteSearchDropdown
              query={query}
              onClose={() => { setQuery(""); setSearchFocused(false); }}
            />
          )}
        </div>

        <button
          type="button"
          className="os-icon-btn"
          title="Benachrichtigungen"
          aria-label="Benachrichtigungen"
        >
          <Bell size={14} strokeWidth={1.8} />
        </button>

        {showAdministration ? (
          <Link
            to="/admin"
            className="os-icon-btn"
            title="Administration öffnen"
            aria-label="Administration öffnen"
            style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}
          >
            <ShieldCheck size={15} strokeWidth={1.8} />
          </Link>
        ) : null}

        <button
          type="button"
          className="os-logout-button"
          title="Abmelden"
          aria-label="Abmelden"
          onClick={handleLogout}
          disabled={logoutMutation.isPending}
        >
          <LogOut size={15} strokeWidth={1.8} aria-hidden="true" />
          <span>Abmelden</span>
        </button>

        {/* Avatar with dropdown menu */}
        <div ref={menuRef} style={{ position: "relative" }}>
          <button
            type="button"
            className="os-avatar"
            title="Konto-Menü"
            aria-label="Konto-Menü"
            onClick={() => setShowMenu(!showMenu)}
            style={{ cursor: "pointer" }}
          >
            {initials}
          </button>

          {showMenu && (
            <div
              className="os-avatar-menu"
              style={{
                position: "absolute",
                top: "calc(100% + 8px)",
                right: 0,
                minWidth: "160px",
                background: "var(--surface-2, #1a1f2e)",
                border: "1px solid var(--border, rgba(255,255,255,0.1))",
                borderRadius: "8px",
                padding: "4px",
                zIndex: 9999,
                boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
              }}
            >
              <Link
                to="/hub/konto"
                onClick={() => setShowMenu(false)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 12px",
                  borderRadius: "6px",
                  fontSize: "12px",
                  color: "rgba(255,255,255,0.75)",
                  textDecoration: "none",
                  transition: "background 0.1s",
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.06)"; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
              >
                <User size={14} strokeWidth={1.8} />
                Konto &amp; Sicherheit
              </Link>
              <Link
                to="/hub/einstellungen"
                onClick={() => setShowMenu(false)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 12px",
                  borderRadius: "6px",
                  fontSize: "12px",
                  color: "rgba(255,255,255,0.75)",
                  textDecoration: "none",
                  transition: "background 0.1s",
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.06)"; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
              >
                <Settings size={14} strokeWidth={1.8} />
                Persönliche Einstellungen
              </Link>
              {showAdministration ? (
                <Link
                  to="/admin"
                  onClick={() => setShowMenu(false)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "8px 12px",
                    borderRadius: "6px",
                    fontSize: "12px",
                    color: "rgba(255,255,255,0.75)",
                    textDecoration: "none",
                    transition: "background 0.1s",
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.06)"; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
                >
                  <ShieldCheck size={14} strokeWidth={1.8} />
                  Administration
                </Link>
              ) : null}
              <div style={{ height: "1px", background: "rgba(255,255,255,0.08)", margin: "4px 8px" }} />
              <button
                type="button"
                onClick={handleLogout}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 12px",
                  borderRadius: "6px",
                  fontSize: "12px",
                  color: "rgba(255,255,255,0.75)",
                  background: "transparent",
                  border: "none",
                  width: "100%",
                  cursor: "pointer",
                  textAlign: "left",
                  transition: "background 0.1s",
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.06)"; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
              >
                <LogOut size={14} strokeWidth={1.8} />
                Abmelden
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
